import { useState, type ReactNode } from 'react'
import { QuickFilterChips } from './QuickFilterChips'
import { SearchInput } from './SearchInput'
import { FilterPanel, type ActiveFilterChip } from './FilterPanel'
import { SortMenu } from './SortMenu'
import { ColumnManagerMenu } from './ColumnManagerMenu'
import { SavedViewsMenu } from './SavedViewsMenu'
import { DensityToggle } from './DensityToggle'
import { ExportMenu } from './ExportMenu'
import type { ColumnDef, Density, ExportFormat, QuickFilterDef, SavedViewsConfig, SortSpec } from './types'

type MenuId = 'filter' | 'sort' | 'columns' | 'views' | 'export' | null

export interface ListControlsBarProps<T> {
  quickFilters: QuickFilterDef[]
  activeQuickFilterIds: Set<string>
  onQuickFilterToggle: (id: string) => void

  search: string
  onSearchChange: (value: string) => void
  searchLabel: string
  searchPlaceholder?: string | undefined

  filterChildren: ReactNode
  activeFilterCount: number
  activeFilterChips: ActiveFilterChip[]
  onRemoveFilterChip: (id: string) => void
  onClearFilters: () => void

  columns: ColumnDef<T>[]
  sort: SortSpec[]
  onSortChange: (next: SortSpec[]) => void

  columnOrder: string[]
  hiddenColumnIds: Set<string>
  pinnedColumnIds: Set<string>
  onToggleColumnHidden: (id: string) => void
  onMoveColumn: (id: string, direction: -1 | 1) => void
  onToggleColumnPin: (id: string) => void
  /** IB-27 S3 — see `ColumnManagerMenuProps` javadoc; optional, only used for the "Reset width" affordance. */
  columnWidths?: Record<string, number> | undefined
  onColumnWidthChange?: ((id: string, width: number) => void) | undefined

  savedViews: SavedViewsConfig

  density: Density
  onDensityChange: (density: Density) => void

  /**
   * IB-98 fix (Slice A review). Optional — CLAUDE.md §4 "every CTA must open a real page or a
   * defined action; no dead buttons." Omitting this prop hides the Export control entirely rather
   * than rendering a menu whose choices silently do nothing (see `CasesListScreen`, which has no
   * export requirement this slice).
   */
  onExport?: ((format: ExportFormat) => void) | undefined
  /** Reporting & Exports honesty rule — see `ExportMenuProps.note`. */
  exportNote?: string | undefined
}

/** Composes every M23 universal list control into one toolbar row. */
export function ListControlsBar<T>(props: ListControlsBarProps<T>) {
  const [openMenu, setOpenMenu] = useState<MenuId>(null)
  const setMenu = (id: MenuId) => (open: boolean) => setOpenMenu(open ? id : null)

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 'var(--iw-space-3)',
        padding: 'var(--iw-space-3) var(--iw-space-5)',
        borderBottom: '1px solid var(--iw-border)',
      }}
    >
      <QuickFilterChips
        filters={props.quickFilters}
        activeIds={props.activeQuickFilterIds}
        onToggle={props.onQuickFilterToggle}
      />
      <span style={{ flex: 1, minWidth: 8 }} />
      <SearchInput
        value={props.search}
        onChange={props.onSearchChange}
        label={props.searchLabel}
        placeholder={props.searchPlaceholder}
      />
      <FilterPanel
        open={openMenu === 'filter'}
        onOpenChange={setMenu('filter')}
        activeCount={props.activeFilterCount}
        activeChips={props.activeFilterChips}
        onRemoveChip={props.onRemoveFilterChip}
        onClear={props.onClearFilters}
      >
        {props.filterChildren}
      </FilterPanel>
      <SortMenu
        columns={props.columns}
        sort={props.sort}
        onSortChange={props.onSortChange}
        open={openMenu === 'sort'}
        onOpenChange={setMenu('sort')}
      />
      <ColumnManagerMenu
        columns={props.columns}
        columnOrder={props.columnOrder}
        hiddenColumnIds={props.hiddenColumnIds}
        pinnedColumnIds={props.pinnedColumnIds}
        onToggleHidden={props.onToggleColumnHidden}
        onMove={props.onMoveColumn}
        onTogglePin={props.onToggleColumnPin}
        columnWidths={props.columnWidths}
        onColumnWidthChange={props.onColumnWidthChange}
        open={openMenu === 'columns'}
        onOpenChange={setMenu('columns')}
      />
      <SavedViewsMenu config={props.savedViews} open={openMenu === 'views'} onOpenChange={setMenu('views')} />
      <DensityToggle density={props.density} onChange={props.onDensityChange} />
      {props.onExport ? (
        <ExportMenu
          onExport={props.onExport}
          open={openMenu === 'export'}
          onOpenChange={setMenu('export')}
          note={props.exportNote}
        />
      ) : null}
    </div>
  )
}
